import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { EnvelopeSimple, MapPin, LinkedinLogo, ArrowRight, PaperPlaneTilt } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { buildPath } from "@/lib/routes";
import { useLang } from "@/hooks/useLang";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { breadcrumbSchema } from "@/lib/schema";

const VISITOR_OPTS = ["0 - 100", "101 - 500", "501 - 2.000", "2.001 - 10.000", "Over 10.000"];

export default function Contact() {
  const { lang, t } = useLang();
  const { profile } = useOutletContext();
  const [form, setForm] = useState({ name: "", email: "", company: "", monthly_visitors: "", message: "" });
  const [loading, setLoading] = useState(false);

  const meta = {
    da: { title: "Kontakt | Magnus Løv Schmidt — SEO Specialist", d: "Kontakt Magnus Løv Schmidt for SEO-hjælp. Skriv via formularen eller fang mig på LinkedIn — svar inden for 24 timer." },
    en: { title: "Contact | Magnus Løv Schmidt — SEO Specialist", d: "Contact Magnus Løv Schmidt for SEO help. Reach out via the form or catch me on LinkedIn — reply within 24 hours." },
  }[lang];

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setLoading(true);
    try {
      await api.createLead(form);
      toast.success(t("contact_success"));
      setForm({ name: "", email: "", company: "", monthly_visitors: "", message: "" });
    } catch (err) {
      toast.error(t("contact_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo routeKey="contact" lang={lang} title={meta.title} description={meta.d}
        jsonLd={[breadcrumbSchema([{ name: t("nav_home"), path: buildPath("home", lang) }, { name: t("nav_contact"), path: buildPath("contact", lang) }])]} />
      <PageHero
        title={lang === "da" ? ["Lad os styrke din", "forretning sammen"] : ["Let's strengthen your", "business together"]}
        description={lang === "da" ? "Fortæl mig om dit projekt eller stil dine spørgsmål. Du får svar inden for 24 timer — medmindre det er weekend." : "Tell me about your project or ask your questions. You'll get a reply within 24 hours — unless it's the weekend."}
        crumbs={[{ label: t("nav_home"), to: "home" }, { label: t("nav_contact") }]} />

      <section className="px-6 md:px-12 lg:px-20 py-16">
        <div className="grid gap-12 lg:grid-cols-5">
          <Reveal className="lg:col-span-3">
            <form onSubmit={submit} data-testid="contact-form" className="space-y-5 rounded-2xl border border-border bg-card p-8">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <Label htmlFor="name">{t("contact_name")} *</Label>
                  <Input id="name" data-testid="contact-name" value={form.name} onChange={set("name")} required className="mt-2" />
                </div>
                <div>
                  <Label htmlFor="email">{t("contact_email")} *</Label>
                  <Input id="email" type="email" data-testid="contact-email" value={form.email} onChange={set("email")} required className="mt-2" />
                </div>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <Label htmlFor="company">{t("contact_company")}</Label>
                  <Input id="company" data-testid="contact-company" value={form.company} onChange={set("company")} className="mt-2" />
                </div>
                <div>
                  <Label>{t("contact_visitors")}</Label>
                  <Select value={form.monthly_visitors} onValueChange={set("monthly_visitors")}>
                    <SelectTrigger data-testid="contact-visitors" className="mt-2">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {VISITOR_OPTS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="message">{t("contact_message")} *</Label>
                <Textarea id="message" data-testid="contact-message" value={form.message} onChange={set("message")} required rows={5} className="mt-2" />
              </div>
              <button type="submit" disabled={loading} data-testid="contact-submit"
                className="group inline-flex items-center justify-center gap-2 rounded-md bg-primary px-7 py-3.5 font-medium text-primary-foreground transition-transform hover:scale-[1.03] disabled:opacity-60">
                <PaperPlaneTilt size={18} weight="bold" />
                {loading ? t("contact_sending") : t("contact_send")}
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="space-y-4">
              <div className="rounded-2xl border border-border bg-card p-7">
                <MapPin size={22} className="text-primary" />
                <div className="mt-4 label-mono text-muted-foreground/60">{t("contact_find")}</div>
                <div className="mt-2 text-foreground">
                  {profile?.address?.street}<br />
                  {profile?.address?.postal_code} {profile?.address?.locality}<br />
                  {profile?.address?.region}
                </div>
              </div>
              <a href={`mailto:${profile?.email}`} className="block rounded-2xl border border-border bg-card p-7 transition-colors hover:border-primary">
                <EnvelopeSimple size={22} className="text-primary" />
                <div className="mt-4 label-mono text-muted-foreground/60">{t("contact_reach")}</div>
                <div className="mt-2 text-foreground">{profile?.email}</div>
              </a>
              <a href={profile?.linkedin} target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin"
                className="group flex items-center justify-between rounded-2xl border border-border bg-card p-7 transition-colors hover:border-primary">
                <div>
                  <LinkedinLogo size={22} weight="fill" className="text-primary" />
                  <div className="mt-4 label-mono text-muted-foreground/60">LinkedIn</div>
                  <div className="mt-2 text-foreground">Magnus Løv Schmidt</div>
                </div>
                <ArrowRight size={20} className="text-muted-foreground group-hover:text-primary" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
